// TCG Attic staff sign-in: Clerk as the OpenID Connect provider behind Saleor's own
// external authentication (api, attic/auth/oidc.py). Nothing here loads Clerk: the API
// lists one external authentication when the door is open, the sign-in route sends the
// browser straight to it, Clerk's hosted screens do the rest, and the code comes back
// through the stock callback route. What this file adds is PKCE, which that door
// requires, and the two notes the route keeps in this tab.

/** The plugin id the API answers with (attic/auth/oidc.py, PLUGIN_ID). */
export const ATTIC_CLERK_PLUGIN_ID = "attic.auth.clerk";

/**
 * Set for the life of the tab when the person signed out on purpose. The sign-in route
 * reads it so a deliberate sign-out lands on a page with a button rather than being sent
 * straight back into Clerk, which still holds its own session and would sign the same
 * person in again without a keystroke.
 */
const SIGNED_OUT_KEY = "atticSignedOut";

export const markSignedOut = (): void => {
  try {
    window.sessionStorage.setItem(SIGNED_OUT_KEY, "1");
  } catch {
    // Storage blocked: the sign-in route will redirect, which is the worse of the two
    // outcomes only on a shared computer, and only until the Clerk session ends.
  }
};

export const clearSignedOut = (): void => {
  try {
    window.sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // As above.
  }
};

export const wasSignedOut = (): boolean => {
  try {
    return window.sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Clerk's instance, remembered off the authorization URL the API handed us, so the
 * doorstep page can link to Clerk's account portal without any configuration of its
 * own. The portal is the Frontend API host with its `clerk` label taken out, which is
 * how Clerk names them: `clerk.tcgattic.com` -> `accounts.tcgattic.com` on the production
 * instance, `<slug>.clerk.accounts.dev` -> `<slug>.accounts.dev` on a development one.
 */
const ISSUER_KEY = "atticClerkIssuer";

export const rememberIssuer = (authorizationUrl: string): void => {
  try {
    window.sessionStorage.setItem(ISSUER_KEY, new URL(authorizationUrl).origin);
  } catch {
    // A URL that does not parse, or storage blocked: the link is simply not offered.
  }
};

export const getAccountPortalUrl = (): string => {
  try {
    const issuer = window.sessionStorage.getItem(ISSUER_KEY);

    if (!issuer) {
      return "";
    }

    const host = new URL(issuer).host;

    if (host.startsWith("clerk.")) {
      return `https://${host.replace(/^clerk\./, "accounts.")}/user`;
    }

    if (host.includes(".clerk.")) {
      return `https://${host.replace(".clerk.", ".")}/user`;
    }

    return "";
  } catch {
    return "";
  }
};

/**
 * PKCE (RFC 7636). The verifier is minted here, kept in this tab's sessionStorage, and
 * never sent anywhere but the API at the code exchange; its S256 challenge rides in the
 * authorization request. So a code that leaks out of the callback URL, or one an attacker
 * obtained for themselves and planted in this tab's callback, exchanges for nothing: the
 * API refuses any exchange whose verifier does not match the challenge it sealed in the
 * state, and Clerk checks the same binding.
 */
const PKCE_VERIFIER_KEY = "atticPkceVerifier";

const base64url = (bytes: Uint8Array): string =>
  window
    .btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * Mint a verifier for a sign-in that is starting, keep it for the callback, and return
 * the challenge to send with the authorization request. Storage blocked: the verifier is
 * lost, the exchange is refused in the API's words, and the person is asked to start
 * again, which is the right outcome for a tab that cannot keep a secret.
 */
export const startPkce = async (): Promise<string> => {
  const random = new Uint8Array(32);

  window.crypto.getRandomValues(random);

  const verifier = base64url(random);
  const digest = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));

  try {
    window.sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  } catch {
    // As above.
  }

  return base64url(new Uint8Array(digest));
};

/** The verifier the current sign-in was started with, once; null when this tab has none. */
export const takePkceVerifier = (): string | null => {
  try {
    const verifier = window.sessionStorage.getItem(PKCE_VERIFIER_KEY);

    window.sessionStorage.removeItem(PKCE_VERIFIER_KEY);

    return verifier;
  } catch {
    return null;
  }
};
